import React, { useState, useEffect } from "react";
import Select from "react-select";
import * as XLSX from "xlsx";
import ReportApi from "../apiProvider/reportApi";
import ProductApi from "../apiProvider/product";
import UserApi from "../apiProvider/userapi";

const ProductReportLayer = () => {
    // Determine default dates
    const today = new Date();
    const firstDayKey = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
    const todayKey = today.toISOString().split('T')[0];

    // State Variables
    const [fromDate, setFromDate] = useState(firstDayKey);
    const [toDate, setToDate] = useState(todayKey);

    const customStyles = {
        control: (provided, state) => ({
            ...provided,
            borderColor: '#3A3530',
            boxShadow: state.isFocused ? '0 0 0 1px #3A3530' : null,
            '&:hover': {
                borderColor: '#3A3530'
            }
        }),
        option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isSelected
                ? '#3A3530'
                : state.isFocused
                    ? '#EEEAE5'
                    : null,
            color: state.isSelected ? 'white' : '#3A3530',
            '&:active': {
                backgroundColor: '#3A3530',
                color: 'white'
            }
        }),
        singleValue: (provided) => ({
            ...provided,
            color: '#3A3530'
        })
    };
    const [products, setProducts] = useState([]);
    const [users, setUsers] = useState([]);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [selectedUser, setSelectedUser] = useState(null);
    const [selectedCurrency, setSelectedCurrency] = useState(localStorage.getItem('adminCurrency') || "");
    const [reportData, setReportData] = useState([]);
    const [loading, setLoading] = useState(false);

    // Pagination State
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10); // set to 10 items per page

    // Date Formatter: DD-MM-YYYY
    const formatDate = (dateString) => {
        if (!dateString) return "N/A";
        const d = new Date(dateString);
        if (isNaN(d.getTime())) return "N/A";
        return `${String(d.getDate()).padStart(2, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}`;
    };

    // Initial Data Fetch
    useEffect(() => {
        fetchDropdowns();
    }, []);

    const fetchDropdowns = async () => {
        try {
            // Fetch Products
            // Using a large limit to grab a substantial list for the dropdown
            const prodRes = await ProductApi.getAllproductList({ limit: 2000 });
            if (prodRes.status && prodRes.response) {
                // Check if response is array or wrapped
                let data = prodRes.response;
                if (data.data && Array.isArray(data.data)) {
                    data = data.data; // Handle recursive data wrapper spread if happens
                } else if (!Array.isArray(data) && data.docs) {
                    data = data.docs; // Handle pagination wrapper
                }

                // If purely array
                if (Array.isArray(data)) {
                    const opts = data.map(p => ({
                        value: p._id,
                        label: `${p.productName} (${p.productCode})`
                    }));
                    setProducts(opts);
                }
            }

            // Fetch Customers
            const userRes = await UserApi.getCustomerList({ limit: 2000 });
            if (userRes.status && userRes.response) {
                // Similarly handle potentially different response structures
                let data = userRes.response;
                if (data.data && Array.isArray(data.data)) {
                    data = data.data;
                } else if (!Array.isArray(data) && data.docs) {
                    data = data.docs;
                }

                if (Array.isArray(data)) {
                    const opts = data.map(u => ({
                        value: u._id,
                        label: `${u.name || 'Unknown'} (${u.phoneNumber || u.email})`
                    }));
                    setUsers(opts);
                }
            }
        } catch (err) {
            console.error("Error fetching dropdowns", err);
        }
    };

    const handleSearch = async () => {
        if (!fromDate || !toDate) {
            alert("Please select both From Date and To Date"); // Simple validation
            return;
        }

        setLoading(true);
        try {
            const payload = {
                fromDate,
                toDate,
                productId: selectedProduct?.value,
                userId: selectedUser?.value,
                currency: selectedCurrency || undefined
            };
            const result = await ReportApi.getProductOrderReport(payload);

            if (result.status && result.response) {
                // Handle standard response wrapper { status, message, data: [] }
                const list = result.response.data || result.response;
                setReportData(Array.isArray(list) ? list : []);
                setCurrentPage(1); // Reset to first page on new search
            } else {
                setReportData([]);
                setCurrentPage(1);
            }
        } catch (error) {
            console.error("Error fetching report", error);
            setReportData([]);
        } finally {
            setLoading(false);
        }
    };

    const handleExport = () => {
        if (!reportData.length) return;

        const exportData = reportData.map(item => ({
            "Order Date": formatDate(item.orderDate),
            "Order ID": item.orderCode || item.orderId,
            "Customer Name": item.customerName || "N/A",
            "Product Name": item.productName || "N/A",
            "Quantity": item.quantity,
            "Unit Price": `${item.currency === 'SGD' ? 'S$' : '₹'}${item.unitPrice?.toFixed(2)}`,
            "Total Price": `${item.currency === 'SGD' ? 'S$' : '₹'}${item.totalPrice?.toFixed(2)}`
        }));

        const ws = XLSX.utils.json_to_sheet(exportData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Product Report");
        XLSX.writeFile(wb, `Product_Order_Report_${fromDate}_to_${toDate}.xlsx`);
    };

    return (
        <div className="container-fluid">
            <div className="row">
                <div className="col-12">
                    <div className="card">
                        {/* <div className="card-header bg-white border-bottom">
                            <h4 className="card-title mb-0">Product Sales Report</h4>
                        </div> */}
                        <div className="card-body">
                            {/* Centralized Filter Section */}
                            <div className="row justify-content-center mb-4">
                                <div className="col-lg-6 col-md-8">
                                    <h5 className="text-center mb-4">Report Filters</h5>

                                    <div className="mb-3">
                                        <label className="form-label fw-bold">From Date <span className="text-danger">*</span></label>
                                        <input
                                            type="date"
                                            className="form-control"
                                            value={fromDate}
                                            onChange={(e) => setFromDate(e.target.value)}
                                        />
                                    </div>

                                    <div className="mb-3">
                                        <label className="form-label fw-bold">To Date <span className="text-danger">*</span></label>
                                        <input
                                            type="date"
                                            className="form-control"
                                            value={toDate}
                                            onChange={(e) => setToDate(e.target.value)}
                                        />
                                    </div>

                                     <div className="mb-3">
                                         <label className="form-label fw-bold">Select Currency</label>
                                         <select
                                             className="form-select"
                                             value={selectedCurrency}
                                             onChange={(e) => setSelectedCurrency(e.target.value)}
                                             style={{ borderColor: '#3A3530' }}
                                         >
                                             <option value="">All Currencies</option>
                                             <option value="INR">INR (₹)</option>
                                             <option value="SGD">SGD (S$)</option>
                                         </select>
                                     </div>

                                     <div className="mb-3">
                                         <label className="form-label fw-bold">Select Product</label>
                                        <Select
                                            options={products}
                                            value={selectedProduct}
                                            onChange={setSelectedProduct}
                                            isClearable
                                            placeholder="All Products"
                                            classNamePrefix="select"
                                            styles={customStyles}
                                        />
                                    </div>

                                    <div className="mb-4">
                                        <label className="form-label fw-bold">Select Customer</label>
                                        <Select
                                            options={users}
                                            value={selectedUser}
                                            onChange={setSelectedUser}
                                            isClearable
                                            placeholder="All Customers"
                                            classNamePrefix="select"
                                            styles={customStyles}
                                        />
                                    </div>

                                    {/* Search Button Block */}
                                    <div className="d-grid" style={{ marginTop: '20px', marginBottom: '20px' }}>
                                        <button
                                            className="btn btn-primary"
                                            onClick={handleSearch}
                                            disabled={loading}
                                        >
                                            {loading ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                    Generating Report...
                                                </>
                                            ) : (
                                                <>
                                                    <i className="bx bx-search-alt-2 me-2"></i> Search Report
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {/* Export Action Bar - Right Aligned */}
                            {reportData.length > 0 && (
                                <div className="d-flex justify-content-end mb-3">
                                    <button
                                        className="btn btn-success d-flex align-items-center gap-2"
                                        onClick={handleExport}
                                    >
                                        <i className="bx bx-file"></i> Export Excel
                                    </button>
                                </div>
                            )}

                            {/* Results Table */}
                            <div className="table-responsive">
                                <table className="table table-bordered table-striped table-hover mb-0 align-middle">
                                    <thead className="table-light">
                                        <tr>
                                            <th className="text-center">S.No</th>
                                            <th>Date</th>
                                            <th>Order ID</th>
                                            <th>Customer Name</th>
                                            <th>Product Name</th>
                                            <th>Quantity</th>
                                            <th className="text-end">Unit Price</th>
                                            <th className="text-end">Total Price</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {reportData.length > 0 ? (
                                            reportData
                                                .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
                                                .map((item, index) => (
                                                    <tr key={index}>
                                                        <td className="text-center">{(currentPage - 1) * itemsPerPage + index + 1}</td>
                                                        <td>{formatDate(item.orderDate)}</td>
                                                        <td>{item.orderCode || item.orderId}</td>
                                                        <td>{item.customerName}</td>
                                                        <td>{item.productName}</td>
                                                        <td className="text-center">{item.quantity}</td>
                                                        <td className="text-end">{item.currency === 'SGD' ? 'S$' : '₹'}{item.unitPrice?.toFixed(2)}</td>
                                                        <td className="text-end fw-bold">{item.currency === 'SGD' ? 'S$' : '₹'}{item.totalPrice?.toFixed(2)}</td>
                                                    </tr>
                                                ))
                                        ) : (
                                            <tr>
                                                <td colSpan="8" className="text-center py-4 text-muted">
                                                    {loading ? "Fetching data..." : "No records found matching criteria"}
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            {/* Pagination */}
                            {reportData.length > 0 && (
                                <div className="d-flex justify-content-between align-items-center mt-4">
                                    <div>
                                        Showing {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, reportData.length)} of {reportData.length} entries
                                    </div>
                                    <nav>
                                        <ul className="pagination mb-0">
                                            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                                <button className="page-link text-secondary" onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}>Previous</button>
                                            </li>
                                            <li className={`page-item ${currentPage === Math.ceil(reportData.length / itemsPerPage) ? 'disabled' : ''}`}>
                                                <button
                                                    className="page-link"
                                                    style={{ backgroundColor: '#3a3530', color: '#fff', borderColor: '#3a3530' }}
                                                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, Math.ceil(reportData.length / itemsPerPage)))}>
                                                    Next
                                                </button>
                                            </li>
                                        </ul>
                                    </nav>
                                </div>
                            )}

                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProductReportLayer;
